import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@gluestack-ui/themed';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createStackNavigator } from '@react-navigation/stack';
import { NavigationContainer, NavigationIndependentTree } from '@react-navigation/native';
import axios from 'axios';

import { DateRangeCalendar } from '@/admin/common/reportFilters/DateRangeCalendar';
import AiTutorDashboard from '@/admin/dashboards/aiTutor/AiTutorDashboard';
import { withDemoReleaseLines } from '@/admin/dashboards/aiTutor/demoReleaseLines';
import {
  loadAiTutorMetricsFixture,
  type AiTutorMetricsFixtureState,
} from '@/admin/common/reportFilters/aiTutorMetricsFixture';

import {
  DENSE_LAB_FILTER_FROM,
  DENSE_LAB_FILTER_TO,
  loadDenseLabMetrics,
} from './denseLabMetrics';
import {
  analyticsLabShowsDatePicker,
  analyticsLabStateToFixtureState,
  parseAnalyticsLabQuery,
  type AnalyticsLabState,
} from './parseAnalyticsLabQuery';

export const ANALYTICS_LAB_PATH = '/visual-lab/analytics';

const Stack = createStackNavigator();

function fixtureRouteParams(state: AnalyticsLabState): Record<string, unknown> {
  switch (state) {
    case 'ready':
      return { excludePasteHeavy: false, excludeFirstTryPasses: false };
    case 'dense':
      return {
        from: DENSE_LAB_FILTER_FROM,
        to: DENSE_LAB_FILTER_TO,
        excludePasteHeavy: false,
        excludeFirstTryPasses: false,
      };
    case 'empty':
      return { from: '2020-01-01T00:00:00.000Z', to: '2020-12-31T00:00:00.000Z' };
    case 'error':
      return { forceError: true };
    default:
      return {};
  }
}

function AnalyticsLabDatePicker() {
  const [from, setFrom] = useState('2026-08-05T00:00:00.000Z');
  const [to, setTo] = useState('2026-08-20T23:59:59.999Z');
  return (
    <Box
      testID="analytics-lab-date-picker"
      alignSelf="flex-start"
      bg="$bgCard"
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$sm"
      p="$3"
      mb="$3"
    >
      <DateRangeCalendar
        from={from}
        to={to}
        onFromChange={setFrom}
        onToChange={setTo}
      />
    </Box>
  );
}

type MetricsLabAdapterState = AiTutorMetricsFixtureState | 'error' | 'dense';

function installMetricsFixtureAdapter(fixtureState: MetricsLabAdapterState) {
  const defaultAdapter = axios.defaults.adapter;
  axios.defaults.adapter = async (config) => {
    const url = config.url ?? '';
    if (url.includes('/reports/metrics')) {
      if (fixtureState === 'error') {
        return Promise.reject(new Error('Visual Lab forced error'));
      }
      let payload = fixtureState === 'dense'
        ? loadDenseLabMetrics()
        : loadAiTutorMetricsFixture(fixtureState);
      if (fixtureState === 'ready') {
        payload = withDemoReleaseLines(payload);
      }
      const body = { data: payload };
      return {
        data: body,
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
      };
    }
    if (typeof defaultAdapter === 'function') {
      return defaultAdapter(config);
    }
    throw new Error('No axios adapter');
  };
  return () => {
    axios.defaults.adapter = defaultAdapter;
  };
}

type MetricsFixtureAdapterProps = {
  fixtureState: MetricsLabAdapterState;
  children: React.ReactNode;
};

type InstalledAdapter = {
  state: MetricsLabAdapterState;
  restore: () => void;
};

/**
 * Installs the fixture axios adapter during render, before children mount and subscribe to queries.
 */
function MetricsFixtureAdapter({ fixtureState, children }: MetricsFixtureAdapterProps) {
  const installedRef = useRef<InstalledAdapter | null>(null);

  if (!installedRef.current || installedRef.current.state !== fixtureState) {
    installedRef.current?.restore();
    installedRef.current = {
      state: fixtureState,
      restore: installMetricsFixtureAdapter(fixtureState),
    };
  }

  useEffect(
    () => () => {
      installedRef.current?.restore();
      installedRef.current = null;
    },
    [],
  );

  return children;
}

/**
 * Visual Lab — production AI Tutor dashboard shell fed by the contract fixture.
 */
export function AnalyticsLabScreen({ search }: { search: string }) {
  const labState = parseAnalyticsLabQuery(search);
  const showDatePicker = analyticsLabShowsDatePicker(search);
  const adapterState: MetricsLabAdapterState = labState === 'dense'
    ? 'dense'
    : analyticsLabStateToFixtureState(labState);
  const initialParams = useMemo(() => fixtureRouteParams(labState), [labState]);

  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: { queries: { retry: false } },
  }), []);

  return (
    <Box flex={1} width="100%" minHeight={0} bg="$bgApp" testID="analytics-lab-shell">
      {showDatePicker ? <AnalyticsLabDatePicker /> : null}
      <QueryClientProvider client={queryClient}>
        <MetricsFixtureAdapter fixtureState={adapterState}>
          <Box
            flex={1}
            width="100%"
            minHeight={720}
            testID="analytics-lab-nav-host"
            sx={{
              _web: {
                minHeight: 'calc(100vh - 48px)',
                height: 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <NavigationIndependentTree>
              <NavigationContainer>
                <Stack.Navigator
                  id="analyticsLab"
                  screenOptions={{
                    headerShown: false,
                    cardStyle: { flex: 1 },
                  }}
                >
                  <Stack.Screen
                    name="AiTutor"
                    component={AiTutorDashboard}
                    initialParams={initialParams}
                  />
                </Stack.Navigator>
              </NavigationContainer>
            </NavigationIndependentTree>
          </Box>
        </MetricsFixtureAdapter>
      </QueryClientProvider>
    </Box>
  );
}
